// src/helpers/pdfPendientesCliente.js

import { jsPDF } from "jspdf";

import {
  fmtDate,
  fmtNum,
  S,
  loadImageAsDataURL,
  drawHeader,
} from "./pdfCommonSanPancracio";

/**
 * Genera un PDF con el resumen de remitos pendientes por CLIENTE.
 *
 * Orden de Cobro:
 * - Pendiente Venta A
 * - Pendiente Venta B
 * - Pendiente Flete
 *
 * Flete ya llega desde el modal como IMPORTE REAL:
 * kgRecibido × tarifaFlete
 */
export async function generarPDFPendientesCliente({
  cliente,
  proveedor = null,
  pendientes = [],
  totales,
  fecha,
  logoUrl = "/img/logo-sanpancracio.png",
  empresa = {},
}) {
  if (!pendientes.length) {
    alert("No hay remitos pendientes para este cliente.");
    return;
  }

  const doc = new jsPDF({
    unit: "mm",
    format: "a4",
  });

  const logoDataURL = await loadImageAsDataURL(logoUrl);

  let y = 12;

  // Encabezado
  drawHeader(doc, y, logoDataURL, empresa);

  y += 34;

  // Título
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);

  doc.text(
    "RESUMEN DE REMITOS PENDIENTES PARA ORDEN DE COBRO",
    105,
    y,
    { align: "center" }
  );

  y += 8;

  // Cliente / proveedor / fecha
  const cliNombre = S(
    cliente?.razonsocial ||
      cliente?.nombre ||
      cliente ||
      "-"
  );

  const provNombre = proveedor
    ? S(
        proveedor?.razonsocial ||
          proveedor?.nombre ||
          proveedor
      )
    : null;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  doc.text(`Cliente: ${cliNombre}`, 12, y);

  if (provNombre) {
    doc.text(
      `Proveedor (filtro): ${provNombre}`,
      12,
      y + 5
    );
  }

  doc.text(
    `Fecha: ${fmtDate(fecha)}`,
    198,
    y,
    { align: "right" }
  );

  y += provNombre ? 12 : 8;

  // --------------------------------------------------
  // TABLA
  // --------------------------------------------------

  const startX = 12;

  const colRem = 22;
  const colFecha = 22;
  const colA = 38;
  const colB = 38;
  const colFlete = 34;
  const colSaldo = 32;

  const totalWidth =
    colRem +
    colFecha +
    colA +
    colB +
    colFlete +
    colSaldo;

  const headerY = y;

  doc.setFillColor(225, 235, 255);
  doc.setDrawColor(180, 180, 200);

  doc.rect(
    startX,
    headerY - 5,
    totalWidth,
    7,
    "F"
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);

  doc.text(
    "Remito",
    startX + 2,
    headerY
  );

  doc.text(
    "Fecha",
    startX + colRem + 2,
    headerY
  );

  doc.text(
    "Pend. Venta A",
    startX +
      colRem +
      colFecha +
      colA -
      2,
    headerY,
    { align: "right" }
  );

  doc.text(
    "Pend. Venta B",
    startX +
      colRem +
      colFecha +
      colA +
      colB -
      2,
    headerY,
    { align: "right" }
  );

  doc.text(
    "Pend. Flete",
    startX +
      colRem +
      colFecha +
      colA +
      colB +
      colFlete -
      2,
    headerY,
    { align: "right" }
  );

  doc.text(
    "Saldo",
    startX + totalWidth - 2,
    headerY,
    { align: "right" }
  );

  y += 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);

  const maxY = 260;

  const filasOrdenadas = [...pendientes].sort(
    (a, b) =>
      new Date(a.fecha || 0) -
      new Date(b.fecha || 0)
  );

  for (const r of filasOrdenadas) {
    if (y > maxY) {
      doc.addPage();
      y = 20;
    }

    const pendA =
      Number(
        r.pendienteVentaA ??
          r.pendienteA ??
          Math.max(
            0,
            Number(r.totalVentaA || 0) -
              Number(r.cobradoVentaA || 0)
          )
      ) || 0;

    const pendB =
      Number(
        r.pendienteVentaB ??
          r.pendienteB ??
          Math.max(
            0,
            Number(r.totalVentaB || 0) -
              Number(r.cobradoVentaB || 0)
          )
      ) || 0;

    const pendFlete =
      Number(
        r.pendienteFlete ??
          Math.max(
            0,
            Number(r.flete || 0) -
              Number(r.cobradoFlete || 0)
          )
      ) || 0;

    const saldo = Number(
      (
        pendA +
        pendB +
        pendFlete
      ).toFixed(2)
    );

    const nroRem =
      r.codrem != null
        ? String(r.codrem).padStart(6, "0")
        : "-";

    const rowY = y;

    doc.setDrawColor(230);

    doc.line(
      startX,
      rowY + 3,
      startX + totalWidth,
      rowY + 3
    );

    doc.text(
      `#${nroRem}`,
      startX + 2,
      rowY + 2
    );

    doc.text(
      fmtDate(r.fecha),
      startX + colRem + 2,
      rowY + 2
    );

    doc.text(
      `$ ${fmtNum(pendA)}`,
      startX +
        colRem +
        colFecha +
        colA -
        2,
      rowY + 2,
      { align: "right" }
    );

    doc.text(
      `$ ${fmtNum(pendB)}`,
      startX +
        colRem +
        colFecha +
        colA +
        colB -
        2,
      rowY + 2,
      { align: "right" }
    );

    doc.text(
      `$ ${fmtNum(pendFlete)}`,
      startX +
        colRem +
        colFecha +
        colA +
        colB +
        colFlete -
        2,
      rowY + 2,
      { align: "right" }
    );

    doc.text(
      `$ ${fmtNum(saldo)}`,
      startX + totalWidth - 2,
      rowY + 2,
      { align: "right" }
    );

    y += 6;
  }

  // --------------------------------------------------
  // RESUMEN
  // --------------------------------------------------

  y += 5;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);

  doc.setDrawColor(120, 120, 160);
  doc.setFillColor(220, 230, 255);

  const resumenY = y;
  const resumenHeight = 17;

  doc.rect(
    startX,
    resumenY - 6,
    totalWidth,
    resumenHeight,
    "F"
  );

  // Línea 1
  doc.text(
    `Σ Venta A: $ ${fmtNum(
      totales?.ventaA
    )}   —   Σ Venta B: $ ${fmtNum(
      totales?.ventaB
    )}`,
    startX + 2,
    resumenY - 1
  );

  // Línea 2
  doc.text(
    `Σ Flete: $ ${fmtNum(
      totales?.flete
    )}`,
    startX + 2,
    resumenY + 4
  );

  // Línea 3
  doc.text(
    `Σ Saldo total: $ ${fmtNum(
      totales?.saldo
    )}`,
    startX + 2,
    resumenY + 9
  );

  // Pie
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);

  doc.text(
    "Documento electrónico generado por el sistema San Pancracio — Resumen para Orden de Cobro",
    12,
    292
  );

  const safeCli = cliNombre.replace(
    /[^\w-]+/g,
    "_"
  );

  const filename =
    `Pendientes_${safeCli || "cliente"}.pdf`;

  doc.save(filename);
}